import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import { PageShell, PageHeader, EmptyState } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import { API, pickContent, sortContent } from "../lib/content";

const SITE = "https://lifeisaproject.global";
const ease = [0.16, 1, 0.3, 1];

/**
 * IdeasPage — reflective / exploratory writing, sourced from the CMS
 * (published ideas only). The legacy essays live here as published CMS
 * records; drafts never appear. Every item shows its content language.
 */
export default function IdeasPage() {
    const { lang } = useLanguage();
    const [items, setItems] = useState([]);

    useEffect(() => {
        axios.get(`${API}/content/ideas`).then((r) => setItems(r.data || [])).catch(() => {});
    }, []);

    const sorted = sortContent(items);

    useJsonLd("itemlist-ideas", sorted.length ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Ideas — Life is a Project",
        url: `${SITE}/ideas`,
        numberOfItems: sorted.length,
        itemListElement: sorted.map((e, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/ideas/${e.slug}`,
            name: e.title,
        })),
    } : null);

    return (
        <PageShell testId="page-ideas" backdrop={<PageBackdrop variant="ideas" />}>
            <PageHeader
                overline={pick3(t3.ideas.overline, lang)}
                title={pick3(t3.ideas.title, lang)}
                kicker={pick3(t3.ideas.lede, lang)}
            />

            <p className="max-w-[62ch] text-[15px] md:text-[16px] leading-[1.7] text-[color:var(--navy)]/80 mb-16">
                {pick3(t3.ideas.body, lang)}
            </p>

            {sorted.length === 0 ? (
                <EmptyState label={pick3(t3.ideas.empty, lang)} />
            ) : (
                <ul data-testid="ideas-list" className="border-t border-[color:var(--line-strong)]">
                    {sorted.map((e, i) => {
                        const { title, subtitle: dek } = pickContent(e, lang);
                        return (
                            <motion.li
                                key={e.slug}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-8%" }}
                                transition={{ duration: 0.7, ease, delay: i * 0.05 }}
                                className="border-b border-[color:var(--line)]"
                            >
                                <Link
                                    to={`/ideas/${e.slug}`}
                                    data-testid="idea-card"
                                    className="grid grid-cols-12 gap-6 py-9 items-baseline group"
                                >
                                    <div className="col-span-3 md:col-span-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                        {e.number || ""}
                                    </div>
                                    <div className="col-span-9 md:col-span-7">
                                        <h3 className="font-display text-[26px] md:text-[36px] leading-[1.08] tracking-[-0.01em] text-[color:var(--navy)] group-hover:italic transition-all duration-500">
                                            {title}
                                        </h3>
                                        {dek && (
                                            <p className="mt-3 max-w-[60ch] text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--navy)]/75">
                                                {dek}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-12 md:col-span-3 md:text-right font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)] flex items-center md:justify-end gap-3">
                                        {e.reading_time && <span>{e.reading_time}</span>}
                                        <span data-testid="content-lang-badge" className="text-[10px] text-[color:var(--navy)]/50 border border-[color:var(--line)] px-1.5 py-0.5">
                                            {(e.language || "en").toUpperCase()}
                                        </span>
                                        <span aria-hidden className="w-4 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-8" />
                                    </div>
                                </Link>
                            </motion.li>
                        );
                    })}
                </ul>
            )}
        </PageShell>
    );
}
